import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { User, Mail, Calendar, X, Search, Plus, Edit, Trash2, CreditCard } from 'lucide-react';
import { getUsers, createUser, updateUser, deleteUser } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const MainContainer = styled.div`
  max-width: 700px;
  margin: 10px auto;
  font-family: 'Roboto', 'Segoe UI', Arial, sans-serif;
  padding: 10px 0;

  @media (max-width: 1024px) {
    max-width: 95%;
    padding: 15px;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 12px;
    margin: 5px auto;
  }

  @media (max-width: 480px) {
    padding: 8px;
    margin: 0 auto;
  }
`;

const Title = styled.h2`
  color: #2c3e50;
  text-align: center;
  margin-bottom: 15px;
  font-weight: 600;
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 8px;
  font-size: 18px;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 20px;
    padding-bottom: 10px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 15px;
    padding-bottom: 8px;
  }
`;

const StatisticsCard = styled.div`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
  padding: 15px;
  margin-bottom: 15px;
  text-align: center;
  border-left: 4px solid var(--primary-color);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 20px;
    margin-bottom: 20px;
    border-radius: 8px;
    border-left-width: 5px;
  }

  @media (max-width: 480px) {
    padding: 15px;
    margin-bottom: 15px;
    border-radius: 6px;
    border-left-width: 4px;
  }
`;

const StatusMessageLabel = styled.div`
  margin: 0 0 15px;
  padding: 8px;
  border-radius: 4px;
  text-align: center;
  font-weight: 500;
  font-size: 11px;
`;

const StatNumber = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 6px;
  }

  @media (max-width: 480px) {
    font-size: 22px;
    margin-bottom: 4px;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #7f8c8d;

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const SearchContainer = styled.div`
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
  padding: 10px;
  margin-bottom: 15px;
  display: flex;
  gap: 8px;
  align-items: center;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 15px;
    margin-bottom: 20px;
    border-radius: 8px;
    gap: 12px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    padding: 12px;
    margin-bottom: 15px;
    gap: 10px;
  }
`;

const SearchInput = styled.div`
  flex-grow: 1;
  position: relative;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const SearchInputField = styled.input`
  width: 100%;
  padding: 8px 8px 8px 30px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 11px;
  transition: all 0.3s;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb, 52, 152, 219), 0.2);
  }

  @media (max-width: 768px) {
    padding: 12px 12px 12px 40px;
    font-size: 16px; /* Важно для iOS */
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    padding: 10px 10px 10px 35px;
    font-size: 16px;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #7f8c8d;

  @media (max-width: 768px) {
    left: 12px;
  }

  @media (max-width: 480px) {
    left: 10px;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--primary-color);
  color: white;
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: 11px;
  transition: all 0.2s;
  touch-action: manipulation;
  white-space: nowrap;
  
  &:hover {
    background-color: var(--primary-color);
    transform: translateY(-1px);
    filter: brightness(0.9);
  }
  
  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 14px;
    border-radius: 6px;
    gap: 6px;
    min-height: 44px;
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
    padding: 10px 14px;
    font-size: 13px;
    min-height: 40px;

    &:hover {
      transform: none;
    }
  }
`;

const UsersSection = styled.div`
  margin-top: 15px;

  @media (max-width: 768px) {
    margin-top: 20px;
  }

  @media (max-width: 480px) {
    margin-top: 15px;
  }
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #eaeaea;
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 15px;
    padding-bottom: 8px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: 12px;
    padding-bottom: 6px;
  }
`;

const UserCard = styled.div`
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
  margin-bottom: 10px;
  border-left: 2px solid var(--primary-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 15px;
    margin-bottom: 15px;
    border-radius: 8px;
    border-left-width: 3px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
    margin-bottom: 12px;
    border-radius: 6px;
    border-left-width: 2px;

    &:hover {
      transform: none;
    }
  }
`;

const UserDetails = styled.div`
  flex-grow: 1;

  @media (max-width: 480px) {
    margin-bottom: 10px;
  }
`;

const UserName = styled.div`
  font-weight: 600;
  color: #2c3e50;
  font-size: 12px;
  margin-bottom: 3px;

  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 5px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 4px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  color: #7f8c8d;
  font-size: 10px;
  margin-bottom: 3px;

  @media (max-width: 768px) {
    font-size: 13px;
    gap: 5px;
    margin-bottom: 5px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    gap: 4px;
    margin-bottom: 4px;
  }
`;

const UserMeta = styled.div`
  color: #7f8c8d;
  font-size: 9px;
  margin-top: 3px;

  @media (max-width: 768px) {
    font-size: 12px;
    margin-top: 5px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    margin-top: 4px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 6px;

  @media (max-width: 768px) {
    gap: 10px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
`;

const Button = styled.button`
  background-color: ${props => props.red ? '#e74c3c' : props.secondary ? '#7f8c8d' : 'var(--primary-color)'};
  color: white;
  padding: ${props => props.small ? '4px 8px' : '6px 12px'};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  font-size: ${props => props.small ? '10px' : '11px'};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  touch-action: manipulation;
  
  &:hover {
    background-color: ${props => props.red ? '#c0392b' : props.secondary ? '#6c7a7a' : 'var(--primary-color)'};
    transform: translateY(-1px);
    filter: brightness(0.9);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: ${props => props.small ? '8px 12px' : '12px 16px'};
    font-size: ${props => props.small ? '13px' : '14px'};
    border-radius: 6px;
    gap: 5px;
    min-height: 44px;
  }

  @media (max-width: 480px) {
    padding: ${props => props.small ? '8px 10px' : '10px 14px'};
    font-size: ${props => props.small ? '12px' : '13px'};
    min-height: 40px;
    flex: 1;

    &:hover {
      transform: none;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 20px 0;
  color: #7f8c8d;
  font-style: italic;
  font-size: 12px;

  @media (max-width: 768px) {
    padding: 30px 20px;
    font-size: 14px;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    padding: 25px 15px;
    font-size: 13px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0 10px;
  flex-wrap: wrap;
  gap: 2px;

  @media (max-width: 768px) {
    margin: 25px 0 15px;
    gap: 4px;
  }

  @media (max-width: 480px) {
    margin: 20px 0 10px;
    gap: 2px;
  }
`;

const PageButton = styled.button`
  background-color: ${props => props.active ? 'var(--primary-color)' : '#f8f9fa'};
  color: ${props => props.active ? 'white' : '#34495e'};
  border: ${props => props.active ? 'none' : '1px solid #e0e0e0'};
  padding: 4px 8px;
  margin: 0 2px;
  border-radius: 3px;
  cursor: pointer;
  font-weight: ${props => props.active ? '600' : '400'};
  transition: all 0.2s;
  font-size: 11px;
  touch-action: manipulation;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-color)' : '#e9ecef'};
    transform: translateY(-1px);
    filter: ${props => props.active ? 'brightness(0.9)' : 'none'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 14px;
    border-radius: 5px;
    margin: 0 3px;
    min-height: 36px;
    min-width: 36px;
  }

  @media (max-width: 480px) {
    padding: 6px 10px;
    font-size: 13px;
    margin: 0 2px;
    min-height: 32px;
    min-width: 32px;

    &:hover {
      transform: none;
    }
  }
`;

// Modal styles с адаптивным отступом
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  padding: 10px;
  
  /* Добавляем отступ слева в зависимости от состояния сайдбара на десктопе */
  @media (min-width: 1025px) {
    margin-left: ${props => props.sidebarCollapsed ? '80px' : '300px'};
    transition: margin-left 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    top: 60px; /* Отступ от TopBar на десктопе */
    height: calc(100vh - 60px); /* Высота минус TopBar */
  }

  @media (max-width: 1024px) {
    margin-left: 0;
    padding: 15px;
    top: 60px; /* Отступ от TopBar на планшетах */
    height: calc(100vh - 60px); /* Высота минус TopBar */
  }

  @media (max-width: 480px) {
    padding: 10px;
    top: 60px; /* Отступ от TopBar на мобильных */
    height: calc(100vh - 60px); /* Высота минус TopBar */
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 6px;
  padding: 12px;
  width: 100%;
  max-width: 900px;
  max-height: calc(100vh - 140px); /* Учитываем TopBar + отступы */
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 8px;
    max-height: calc(100vh - 120px); /* Меньше отступ на планшетах */
  }

  @media (max-width: 480px) {
    padding: 15px;
    border-radius: 6px;
    max-height: calc(100vh - 100px); /* Минимальный отступ на мобильных */
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    margin-bottom: 18px;
  }

  @media (max-width: 480px) {
    margin-bottom: 15px;
  }
`;

const ModalTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #7f8c8d;
  cursor: pointer;
  padding: 3px;
  touch-action: manipulation;
  
  &:hover {
    color: #2c3e50;
  }

  @media (max-width: 768px) {
    padding: 5px;
  }

  @media (max-width: 480px) {
    padding: 4px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 10px;
    margin-bottom: 10px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 8px;
  
  label {
    display: block;
    margin-bottom: 3px;
    font-weight: 500;
    color: #34495e;
    font-size: 10px;
  }
  
  input, select, textarea {
    width: 100%;
    padding: 4px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 10px;
    transition: all 0.3s;
    box-sizing: border-box;
    
    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb, 52, 152, 219), 0.2);
    }
  }
  
  textarea {
    min-height: 40px;
    resize: vertical;
  }

  @media (max-width: 768px) {
    margin-bottom: 12px;

    label {
      font-size: 14px;
      margin-bottom: 6px;
      font-weight: 600;
    }

    input, select, textarea {
      padding: 12px;
      font-size: 16px; /* Важно для iOS */
      border-radius: 6px;
    }

    textarea {
      min-height: 60px;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 10px;

    label {
      font-size: 13px;
      margin-bottom: 5px;
    }

    input, select, textarea {
      padding: 10px;
      font-size: 16px;
      border-radius: 5px;
    }

    textarea {
      min-height: 50px;
    }
  }
`;

const FullWidthFormGroup = styled(FormGroup)`
  grid-column: 1 / -1;
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 8px;
  margin-top: 2px;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 12px;
    margin-top: 4px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    margin-top: 3px;
  }
`;

const InfoCard = styled.div`
  background: var(--primary-color);
  background: linear-gradient(135deg, rgba(var(--primary-color-rgb, 52, 152, 219), 0.1) 0%, rgba(var(--primary-color-rgb, 52, 152, 219), 0.05) 100%);
  border: 1px solid rgba(var(--primary-color-rgb, 52, 152, 219), 0.2);
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    padding: 12px;
    margin-bottom: 18px;
    border-radius: 6px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    margin-bottom: 15px;
    border-radius: 5px;
  }
`;

const InfoText = styled.div`
  color: var(--primary-color);
  font-size: 10px;
  
  strong {
    font-size: 11px;
    font-family: monospace;
  }
  
  .note {
    font-size: 8px;
    margin-top: 2px;
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    font-size: 14px;

    strong {
      font-size: 15px;
    }

    .note {
      font-size: 12px;
      margin-top: 4px;
    }
  }

  @media (max-width: 480px) {
    font-size: 13px;

    strong {
      font-size: 14px;
    }

    .note {
      font-size: 11px;
      margin-top: 3px;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 12px;

  @media (max-width: 768px) {
    gap: 12px;
    margin-top: 20px;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 10px;
    margin-top: 15px;
  }
`;

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [errors, setErrors] = useState({});
  const { showNotification } = useNotification();

  // Добавляем состояние для отслеживания сайдбара
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Form data for editing/adding
  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    middleName: '',
    gender: 'not_specified',
    birthYear: '',
    nationality: '',
    address: '',
    education: 'secondary',
    socialStatus: 'student',
    workplace: '',
    academicDegree: '',
    studyPlace: '',
    email: '',
    password: '',
    avatar: null
  });

  // Пагинация
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 3;

  const genderOptions = [
    { value: 'male', label: 'Мужской' },
    { value: 'female', label: 'Женский' },
    { value: 'other', label: 'Другое' },
    { value: 'not_specified', label: 'Не указывать' }
  ];

  const nationalityOptions = [
    'Казах', 'Русский', 'Украинец', 'Узбек', 'Татар', 'Немец', 'Кореец', 'Турок', 'Другое'
  ];

  const educationOptions = [
    { value: 'elementary', label: 'Начальное' },
    { value: 'secondary', label: 'Среднее' },
    { value: 'vocational', label: 'Средне-специальное' },
    { value: 'higher', label: 'Высшее' },
    { value: 'academic_degree', label: 'Ученая степень' }
  ];

  const socialStatusOptions = [
    { value: 'student', label: 'Студент' },
    { value: 'employed', label: 'Работающий' },
    { value: 'unemployed', label: 'Безработный' },
    { value: 'pensioner', label: 'Пенсионер' },
    { value: 'school_student', label: 'Школьник' },
    { value: 'entrepreneur', label: 'ИП' },
    { value: 'civil_servant', label: 'Госслужащий' },
    { value: 'other', label: 'Другое' }
  ];

  // Загрузка пользователей
  useEffect(() => {
    fetchUsers();
  }, []);

  // Получаем состояние сайдбара из localStorage при загрузке компонента
  useEffect(() => {
    const collapsed = JSON.parse(localStorage.getItem('sidebarCollapsed') || 'true');
    setIsSidebarCollapsed(collapsed);

    // Слушаем изменения в localStorage для синхронизации состояния
    const handleStorageChange = () => {
      const collapsed = JSON.parse(localStorage.getItem('sidebarCollapsed') || 'true');
      setIsSidebarCollapsed(collapsed);
    };

    window.addEventListener('storage', handleStorageChange);

    // Также добавим кастомное событие для внутренних изменений
    const handleSidebarToggle = () => {
      const collapsed = JSON.parse(localStorage.getItem('sidebarCollapsed') || 'true');
      setIsSidebarCollapsed(collapsed);
    };

    window.addEventListener('sidebarToggled', handleSidebarToggle);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sidebarToggled', handleSidebarToggle);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
      showNotification("Ошибка загрузки пользователей", "error");
    }
  };

  // Фильтрация пользователей
  useEffect(() => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(user =>
        `${user.lastName} ${user.firstName} ${user.middleName || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.libraryCard.includes(searchTerm)
      );
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [users, searchTerm]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Фамилия обязательна';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Имя обязательно';
    }

    if (!formData.birthYear) {
      newErrors.birthYear = 'Год рождения обязателен';
    } else {
      const year = parseInt(formData.birthYear);
      const currentYear = new Date().getFullYear();
      if (year < 1900 || year > currentYear) {
        newErrors.birthYear = 'Некорректный год рождения';
      }
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Адрес обязателен';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email обязателен';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Некорректный email';
    }

    if (!editingUser && !formData.password.trim()) {
      newErrors.password = 'Пароль обязателен';
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Пароль должен содержать минимум 6 символов';
    }

    if (formData.avatar && formData.size > 15 * 1024 * 1024) {
      newErrors.avatar = "Файл слишком большой. Максимум 15MB"
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const submitData = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (key !== 'avatar' && key !== 'password') {
        submitData.append(key, value)
      }
    });

    if (formData.password && formData.password.trim() !== '') {
      submitData.append('password', formData.password);
    }
    if (formData.avatar) {
      submitData.append("avatar", formData.avatar)
    }

    try {
      if (editingUser) {
        const response = await updateUser(editingUser.id, submitData);
        if (response) {
          await fetchUsers();
          closeModal();
          showNotification("Данные пользователя обновлены", "success");
        }
      } else {
        const response = await createUser(submitData);
        if (response) {
          await fetchUsers();
          closeAddModal();
          showNotification("Пользователь успешно создан", "success");
        }
      }
    } catch (error) {
      showNotification("Ошибка при сохранении", "error");
      console.error('Ошибка при сохранении пользователя:', error);
      setErrors({ general: 'Ошибка при сохранении. Попробуйте еще раз.' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      lastName: '',
      firstName: '',
      middleName: '',
      gender: 'not_specified',
      birthYear: '',
      nationality: '',
      address: '',
      education: 'secondary',
      socialStatus: 'student',
      workplace: '',
      academicDegree: '',
      studyPlace: '',
      email: '',
      password: '',
      avatar: null
    });
    setEditingUser(null);
    setErrors({});
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      lastName: user.lastName || '',
      firstName: user.firstName || '',
      middleName: user.middleName || '',
      gender: user.gender || 'not_specified',
      birthYear: user.birthYear?.toString() || '',
      nationality: user.nationality || '',
      address: user.address || '',
      education: user.education || 'secondary',
      socialStatus: user.socialStatus || 'student',
      workplace: user.workplace || '',
      academicDegree: user.academicDegree || '',
      studyPlace: user.studyPlace || '',
      email: user.email || '',
      password: '',
      avatar: null
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этого пользователя?')) {
      try {
        await deleteUser(id);
        showNotification('Пользователь успешно удален', 'success');
        await fetchUsers();

        // Корректировка пагинации
        const updatedUsers = users.filter(user => user.id !== id);
        const maxPage = Math.ceil(updatedUsers.length / usersPerPage);
        if (currentPage > maxPage && currentPage > 1) {
          setCurrentPage(maxPage);
        }
      } catch (error) {
        console.error('Ошибка при удалении:', error);
      }
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    resetForm();
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    resetForm();
  };


  const formatGender = (gender) => {
    const option = genderOptions.find(opt => opt.value === gender);
    return option ? option.label : gender;
  };

  const formatEducation = (education) => {
    const option = educationOptions.find(opt => opt.value === education);
    return option ? option.label : education;
  };

  const formatSocialStatus = (status) => {
    const option = socialStatusOptions.find(opt => opt.value === status);
    return option ? option.label : status;
  };

  // Пагинация
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <MainContainer>
      <Title>Управление пользователями</Title>


      {/* Статистика */}
      <StatisticsCard>
        <StatNumber>{users.length}</StatNumber>
        <StatLabel>Всего пользователей</StatLabel>
      </StatisticsCard>

      {/* Поиск и кнопка добавления */}
      <SearchContainer>
        <SearchInput>
          <SearchIcon>
            <Search size={14} />
          </SearchIcon>
          <SearchInputField
            type="text"
            placeholder="Поиск по имени, email или читательскому билету..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInput>
        <AddButton onClick={() => setIsAddModalOpen(true)}>
          <Plus size={14} />
          Добавить пользователя
        </AddButton>
      </SearchContainer>

      <UsersSection data-list-section>
        <SectionTitle>Зарегистрированные пользователи ({filteredUsers.length})</SectionTitle>

        {filteredUsers.length > 0 ? (
          <>
            {currentUsers.map((user) => (
              <UserCard key={user.id}>
                <UserDetails>
                  <UserName>
                    {user.lastName} {user.firstName} {user.middleName || ''}
                  </UserName>
                  <UserInfo>
                    <Mail size={10} />
                    {user.email}
                  </UserInfo>
                  <UserInfo>
                    <CreditCard size={10} />
                    Читательский билет: {user.libraryCard}
                  </UserInfo>
                  <UserInfo>
                    <Calendar size={10} />
                    Регистрация: {new Date(user.createdAt).toLocaleDateString('ru-RU')}
                  </UserInfo>
                  {user.birthYear && (
                    <UserMeta>
                      Год рождения: {user.birthYear} | {formatGender(user.gender)} | {formatEducation(user.education)}
                    </UserMeta>
                  )}
                </UserDetails>

                <ActionButtons>
                  <Button
                    small
                    onClick={() => handleEdit(user)}
                    disabled={loading}
                  >
                    <Edit size={10} />
                    Редактировать
                  </Button>
                  <Button
                    small
                    onClick={() => handleDelete(user.id)}
                    disabled={loading}
                    red
                  >
                    <Trash2 size={10} />
                    Удалить
                  </Button>
                </ActionButtons>
              </UserCard>
            ))}

            {totalPages > 1 && (
              <PaginationContainer>
                <PageButton
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  &laquo;
                </PageButton>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                  <PageButton
                    key={number}
                    active={currentPage === number}
                    onClick={() => paginate(number)}
                  >
                    {number}
                  </PageButton>
                ))}

                <PageButton
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  &raquo;
                </PageButton>
              </PaginationContainer>
            )}
          </>
        ) : (
          <EmptyState>
            {searchTerm
              ? 'По вашему запросу пользователи не найдены.'
              : 'Пользователей пока нет.'
            }
          </EmptyState>
        )}
      </UsersSection>

      {/* Модальное окно добавления пользователя */}
      {isAddModalOpen && (
        <ModalOverlay sidebarCollapsed={isSidebarCollapsed}>
          <ModalContainer>
            <ModalHeader>
              <ModalTitle>Добавление пользователя</ModalTitle>
              <CloseButton onClick={closeAddModal}>
                <X size={18} />
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleSubmit}>
              {errors.general && (
                <StatusMessageLabel className="error">
                  {errors.general}
                </StatusMessageLabel>
              )}

              <FormGrid>
                <FormGroup>
                  <label>Фамилия *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Фамилия"
                  />
                  {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <label>Имя *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Имя"
                  />
                  {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <label>Отчество</label>
                  <input
                    type="text"
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Отчество"
                  />
                </FormGroup>

                <FormGroup>
                  <label>Пол</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {genderOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Год рождения *</label>
                  <input
                    type="number"
                    name="birthYear"
                    min="1900"
                    max={new Date().getFullYear()}
                    value={formData.birthYear}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Год"
                  />
                  {errors.birthYear && <ErrorMessage>{errors.birthYear}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <label>Национальность</label>
                  <select
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="">Выберите</option>
                    {nationalityOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Образование *</label>
                  <select
                    name="education"
                    value={formData.education}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {educationOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Соц. статус *</label>
                  <select
                    name="socialStatus"
                    value={formData.socialStatus}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {socialStatusOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Место работы</label>
                  <input
                    type="text"
                    name="workplace"
                    value={formData.workplace}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Место работы"
                  />
                </FormGroup>

                <FormGroup>
                  <label>Научная степень</label>
                  <input
                    type="text"
                    name="academicDegree"
                    value={formData.academicDegree}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Степень"
                  />
                </FormGroup>

                <FormGroup>
                  <label>Место учебы</label>
                  <input
                    type="text"
                    name="studyPlace"
                    value={formData.studyPlace}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Место учебы"
                  />
                </FormGroup>

                <FormGroup>
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Email"
                  />
                  {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <label>Пароль *</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Пароль"
                  />
                  {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <label>Аватар</label>
                  <input
                    type="file"
                    name="avatar"
                    onChange={(e) => setFormData({ ...formData, avatar: e.target.files[0] })}
                    disabled={loading}
                  />
                  {errors.avatar && <ErrorMessage>{errors.avatar}</ErrorMessage>}
                </FormGroup>
              </FormGrid>

              <FullWidthFormGroup>
                <label>Адрес проживания *</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Введите адрес проживания"
                />
                {errors.address && <ErrorMessage>{errors.address}</ErrorMessage>}
              </FullWidthFormGroup>

              <ButtonGroup>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Добавление...' : 'Добавить пользователя'}
                </Button>
                <Button
                  type="button"
                  onClick={closeAddModal}
                  disabled={loading}
                  secondary
                >
                  Отмена
                </Button>
              </ButtonGroup>
            </form>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* Модальное окно редактирования пользователя */}
      {isModalOpen && (
        <ModalOverlay sidebarCollapsed={isSidebarCollapsed}>
          <ModalContainer>
            <ModalHeader>
              <ModalTitle>Редактирование пользователя</ModalTitle>
              <CloseButton onClick={closeModal}>
                <X size={18} />
              </CloseButton>
            </ModalHeader>

            {editingUser && (
              <InfoCard>
                <InfoText>
                  <strong>Читательский билет: {editingUser.libraryCard}</strong>
                  <div className="note">Читательский билет не подлежит изменению</div>
                </InfoText>
              </InfoCard>
            )}

            <form onSubmit={handleSubmit}>
              {errors.general && (
                <StatusMessageLabel className="error">
                  {errors.general}
                </StatusMessageLabel>
              )}

              <FormGrid>
                <FormGroup>
                  <label>Фамилия *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Фамилия"
                  />
                  {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <label>Имя *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Имя"
                  />
                  {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <label>Отчество</label>
                  <input
                    type="text"
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Отчество"
                  />
                </FormGroup>

                <FormGroup>
                  <label>Пол</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {genderOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Год рождения *</label>
                  <input
                    type="number"
                    name="birthYear"
                    min="1900"
                    max={new Date().getFullYear()}
                    value={formData.birthYear}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Год"
                  />
                  {errors.birthYear && <ErrorMessage>{errors.birthYear}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <label>Национальность</label>
                  <select
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="">Выберите</option>
                    {nationalityOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Образование *</label>
                  <select
                    name="education"
                    value={formData.education}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {educationOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Соц. статус *</label>
                  <select
                    name="socialStatus"
                    value={formData.socialStatus}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {socialStatusOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </FormGroup>

                <FormGroup>
                  <label>Место работы</label>
                  <input
                    type="text"
                    name="workplace"
                    value={formData.workplace}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Место работы"
                  />
                </FormGroup>

                <FormGroup>
                  <label>Научная степень</label>
                  <input
                    type="text"
                    name="academicDegree"
                    value={formData.academicDegree}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Степень"
                  />
                </FormGroup>

                <FormGroup>
                  <label>Место учебы</label>
                  <input
                    type="text"
                    name="studyPlace"
                    value={formData.studyPlace}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Место учебы"
                  />
                </FormGroup>

                <FormGroup>
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Email"
                  />
                  {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <label>Новый пароль</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Оставьте пустым"
                  />
                  {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <label>Аватар</label>
                  <input
                    type="file"
                    name="avatar"
                    onChange={(e) => setFormData({ ...formData, avatar: e.target.files[0] })}
                    disabled={loading}
                  />
                  {errors.avatar && <ErrorMessage>{errors.avatar}</ErrorMessage>}
                </FormGroup>
              </FormGrid>

              <FullWidthFormGroup>
                <label>Адрес проживания *</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Введите адрес проживания"
                />
                {errors.address && <ErrorMessage>{errors.address}</ErrorMessage>}
              </FullWidthFormGroup>

              <ButtonGroup>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Сохранение...' : 'Сохранить изменения'}
                </Button>
                <Button
                  type="button"
                  onClick={closeModal}
                  disabled={loading}
                  secondary
                >
                  Отмена
                </Button>
              </ButtonGroup>
            </form>
          </ModalContainer>
        </ModalOverlay>
      )}
    </MainContainer>
  );
}

export default UserManagement;